import { useState, useEffect, useCallback } from 'react';

export interface UseTutorialOptions {
  storageKey: string;
  autoStart?: boolean;
  delay?: number;
}

export interface UseTutorialReturn {
  showTutorial: boolean;
  startTutorial: () => void;
  completeTutorial: () => void;
  skipTutorial: () => void;
  resetTutorial: () => void;
}

const useTutorial = ({
  storageKey,
  autoStart = true,
  delay = 500,
}: UseTutorialOptions): UseTutorialReturn => {
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (!autoStart) return;

    const tutorialCompleted = localStorage.getItem(storageKey);
    if (!tutorialCompleted) {
      const timer = setTimeout(() => setShowTutorial(true), delay);
      return () => clearTimeout(timer);
    }
  }, [storageKey, autoStart, delay]);

  const startTutorial = useCallback(() => {
    setShowTutorial(true);
  }, []);

  const completeTutorial = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    setShowTutorial(false);
  }, [storageKey]);

  const skipTutorial = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    setShowTutorial(false);
  }, [storageKey]);

  const resetTutorial = useCallback(() => {
    localStorage.removeItem(storageKey);
    setShowTutorial(false);
  }, [storageKey]);

  return {
    showTutorial,
    startTutorial,
    completeTutorial,
    skipTutorial,
    resetTutorial,
  };
};

export default useTutorial;
